// @flow

import React, { useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { Input, Label, View, Text } from "@components";

import * as selectors from "@modules/profile/selectors";
import actionCreators from "@modules/profile/actionCreator";
import type { MyAbsenceDraft } from "@modules/profile/reducer/my-absences/types";
import QuestionAnswerContainer from "@components/question-answer-container";
import ReferralQuestions from "./referral-questions";

import { type StepProps } from "@components";

type PropsFromState = $Exact<{
  draft: MyAbsenceDraft | null,
  standardQuestions: Array<any> | null,
  dynamicQuestion: Array<any> | null,
  referralQuestion: Array<any> | null,
}>;

type PropsFromDispatch = $Exact<{
  onUpdateDraft: typeof actionCreators.SetMyAbsenceDraft,
}>;

type Props = $Exact<{
  ...StepProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
  clientId: number,
  answers: Function,
  updateAnswers: Function,
}>;

const buildPreviousAnswers = (questions?: Array<any> | null): Object => {
  return (questions || []).reduce((acc, question) => {
    if (question.previousAnswer && question.previousAnswer.value != null) {
      acc[question.id] = {
        answer: question.previousAnswer.value,
        id: question.id,
      };
    }

    return acc;
  }, {});
};

const MyAdditionalInformation = (props: Props) => {
  const { answers, updateAnswers } = props;

  useEffect(() => {
    const update = {};

    const standardPreset = buildPreviousAnswers(props.standardQuestions);
    const existingStandard = answers.standard?.answers || {};
    if (Object.keys(standardPreset).some(id => !(id in existingStandard))) {
      update.standard = { answers: { ...standardPreset, ...existingStandard } };
    }

    const dynamicPreset = buildPreviousAnswers(props.dynamicQuestion);
    const existingDynamic = answers.dynamic?.answers || {};
    if (Object.keys(dynamicPreset).some(id => !(id in existingDynamic))) {
      update.dynamic = { answers: { ...dynamicPreset, ...existingDynamic } };
    }

    if (Object.keys(update).length) {
      updateAnswers(update);
    }
  }, [props.standardQuestions, props.dynamicQuestion]);

  const getStandardQuestions = (newAnswers?: Object) => {
    const currentAnswers = newAnswers?.answers || answers.standard?.answers;
    const initialQuestions = (props.standardQuestions || []).filter(sq => !sq.dependsOn);
    const additionalQuestions = (props.standardQuestions || []).filter(sq => {
      const questionToShow =
        sq.dependsOn &&
        currentAnswers &&
        currentAnswers[sq.dependsOn.dependencyId] &&
        String(currentAnswers[sq.dependsOn.dependencyId].answer) === sq.dependsOn.activationValue;

      return questionToShow;
    });

    const questionsToShow = [...initialQuestions, ...additionalQuestions];

    if (questionsToShow.length) {
      // first comes CONFIRMATION question (fit note - main one) and after that all others sorted by id
      const sortedQuestions = questionsToShow.sort((a, b) => {
        if (a.type === "CONFIRMATION" || b.type === "CONFIRMATION") {
          return a.type === "CONFIRMATION" ? -1 : 1;
        }
        return a.id - b.id;
      });

      return sortedQuestions;
    }

    return props.standardQuestions;
  };

  const handleChangeStandardAnswers = (values: Object) => {
    const currentQuestions = getStandardQuestions(values);
    // we need to filter out already set answers if some of the questions are not on the screen anymore
    const currentAnswers = (currentQuestions || []).reduce(
      (acc, el) => {
        if (values?.answers && values.answers[el.id]) {
          return {
            ...acc,
            answers: {
              ...acc.answers,
              [el.id]: values.answers[el.id],
            },
          };
        }

        return acc;
      },
      { answers: {} },
    );

    updateAnswers({ standard: currentAnswers });
  };

  const handleChangeDynamicAnswers = (values: Object) => {
    updateAnswers({ dynamic: values });
  };

  const handleChangeReferralQuestions = (values: Object) => {
    updateAnswers({ referrals: values });
  };

  const handleAdditionalInfoUpdate = (e: any) => {
    updateAnswers({ additionalInfo: e.target.value });
  };

  if (!props.editable) {
    return null;
  }

  return (
    <View flex={1} justifyContent="center">
      {!!getStandardQuestions()?.length && (
        <QuestionAnswerContainer
          questions={getStandardQuestions()}
          onChange={handleChangeStandardAnswers}
          answers={answers.standard?.answers || {}}
          columnsPerRow={1}
        />
      )}
      {!!props.dynamicQuestion?.length && (
        <QuestionAnswerContainer
          questions={props.dynamicQuestion}
          onChange={handleChangeDynamicAnswers}
          answers={answers.dynamic?.answers || {}}
          columnsPerRow={1}
        />
      )}
      {!!props.referralQuestion?.length && (
        <View gap={1}>
          <Text type="h5" className="text-center">
            {props.t("REFERRAL_APPROVABLE_TYPE")}
          </Text>
          <ReferralQuestions
            clientId={props.clientId}
            referrals={props.referralQuestion}
            onChange={handleChangeReferralQuestions}
          />
        </View>
      )}
      <View padding="0 2.5rem">
        <Label for="additional-info">{props.t("MY_ABSENCES_ADDITIONAL_QUESTION")}</Label>
        <Input
          id="additional-info"
          value={answers.additionalInfo}
          onChange={handleAdditionalInfoUpdate}
        />
      </View>
    </View>
  );
};

const mapStateToProps = (state): PropsFromState => ({
  draft: selectors.myAbsenceDraft(state),
  standardQuestions: selectors.myAbsenceStandardQuestion(state),
  dynamicQuestion: selectors.myAbsenceDynamicQuestion(state),
  referralQuestion: selectors.myAbsenceReferralQuestion(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  onUpdateDraft: actionCreators.SetMyAbsenceDraft,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(MyAdditionalInformation);
